import { Box, Flex, Text } from "@chakra-ui/layout";
import { Col, Row } from "antd";
import Field from "components/Input/Field";
import {
  ComposicaoContaListaForm,
  ComposicaoItem,
} from "core/features/conta/typings";
import { OperacaoEnum } from "core/resources/enums/conta";
import colors from "core/resources/theme/colors";
import { formatMoney } from "infra/helpers/number";
import { useMemo } from "react";
import {
  Control,
  ControllerRenderProps,
  UseFormGetValues,
} from "react-hook-form";
import { AiOutlinePlusCircle } from "react-icons/ai";
import ComposicaoContaListaItem from "./ComposicaoContaListaItem";
import Title from "./Title";
interface ComposicaoContaListaProps<T extends ComposicaoContaListaForm> {
  operacao: OperacaoEnum;
  form: ComposicaoContaListaForm;
  control: Control<T>;
  onAdd: (ev: any) => any;
  total?: number;
  getValues: UseFormGetValues<{ composicoes: ComposicaoItem[] }>;
  showComplemento?: boolean;
}

function ComposicaoContaLista<T extends ComposicaoContaListaForm>({
  form,
  operacao,
  control,
  onAdd,
  total,
  getValues,
  showComplemento = true,
}: ComposicaoContaListaProps<T>) {
  const _total = useMemo(() => {
    if (total) {
      return total;
    }

    if (!form.composicoes?.length) {
      return 0;
    }

    let result = form.composicoes.reduce(
      (acc: number, next: ComposicaoItem) => {
        if (operacao === OperacaoEnum.Receita) {
          acc = acc + (next.valor || 0) * (next.operacao || 1);
        } else {
          acc = acc + (next.valor || 0);
        }

        return acc;
      },
      0
    );

    return result;
  }, [form.composicoes, operacao, total]);

  const onEditRow = (
    field: ControllerRenderProps<any, any>,
    value: Partial<ComposicaoItem>,
    index: number
  ) => {
    const copy = [...getValues().composicoes];

    copy.splice(index, 1, { ...form.composicoes[index], ...value });

    field.onChange(copy);
  };

  const onRemoveItem = (
    field: ControllerRenderProps<any, any>,
    index: number
  ) => {
    const copy = [...getValues().composicoes];

    copy.splice(index, 1);

    field.onChange(copy);
  };

  return (
    <>
      <Row align="middle">
        <Col>
          <Title label="Composição da Conta" mt={6} />
        </Col>
        <Col style={{ marginLeft: "10px" }}>
          <AiOutlinePlusCircle
            fontSize={28}
            color={colors.primary}
            onClick={onAdd}
            cursor="pointer"
          />
        </Col>
      </Row>

      <Box mb={4}>
        <Field
          control={control}
          name="composicoes"
          rules={{
            required: {
              value: true,
              message: "Informe pelo menos uma composição",
            },
            validate: (v) =>
              v.every(
                (item: ComposicaoItem) =>
                  !!item.categoriaFinanceiraId && !!item.valor
              )
                ? true
                : "Todas as categorias devem estar preenchidas",
          }}
          render={({ field, isInvalid }) => (
            <>
              {form.composicoes?.map(
                (composicao: ComposicaoItem, index: number) => (
                  <ComposicaoContaListaItem
                    index={index}
                    showComplemento={showComplemento}
                    operacao={operacao}
                    complemento={composicao?.complemento ?? ""}
                    centroDeCustoId={composicao?.centroDeCustoId ?? ""}
                    operacaoComposicao={composicao?.operacao}
                    length={form.composicoes.length}
                    valor={composicao?.valor}
                    isInvalid={isInvalid}
                    valueSelect={composicao?.categoriaFinanceiraId}
                    key={`${composicao?.categoriaFinanceiraId}-${index}`}
                    onChangeInput={(v: any) =>
                      onEditRow(field, { complemento: v.target.value }, index)
                    }
                    onChangeSelect={(v, item) => {
                      onEditRow(
                        field,
                        {
                          categoriaFinanceiraId: v,
                          operacao: item?.operacao || null,
                          centroDeCustoId: item?.centroDeCustoId || null,
                        },
                        index
                      );
                    }}
                    onChangeValue={(v: any) =>
                      onEditRow(field, { valor: v }, index)
                    }
                    onChangeCentroDeCusto={(v) => {
                      onEditRow(field, { centroDeCustoId: v }, index);
                    }}
                    onRemoveItem={() => onRemoveItem(field, index)}
                  />
                )
              )}

              {form.composicoes?.length ? (
                <Flex gap={6} mt={3}>
                  <Flex flex={1} />
                  {form.multa &&
                  form.calcularJurosAutomatico &&
                  !form.dataPagamento ? (
                    <Flex justifyContent="flex-end">
                      Multa:
                      <Text ml={1} color={colors.green1000} fontWeight="bold">
                        {formatMoney(form.multa)}
                      </Text>
                    </Flex>
                  ) : null}

                  {form.juros &&
                  form.calcularJurosAutomatico &&
                  !form.dataPagamento ? (
                    <Flex justifyContent="flex-end">
                      Juros:
                      <Text ml={1} color={colors.green1000} fontWeight="bold">
                        {formatMoney(form.juros)}
                      </Text>
                    </Flex>
                  ) : null}

                  {form.valorTaxaCobranca &&
                  form.calcularJurosAutomatico &&
                  !form.dataPagamento ? (
                    <Flex justifyContent="flex-end">
                      Taxa de Cobrança:
                      <Text ml={1} color={colors.green1000} fontWeight="bold">
                        {formatMoney(form.valorTaxaCobranca)}
                      </Text>
                    </Flex>
                  ) : null}

                  <Flex justifyContent="flex-end">
                    Valor total{" "}
                    <Text ml={1} color={colors.black900} fontWeight="bold">
                      {formatMoney(_total)}
                    </Text>
                  </Flex>
                </Flex>
              ) : null}
            </>
          )}
        />
      </Box>
    </>
  );
}

export default ComposicaoContaLista;
